import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { cn, TouchNumberField } from '@rematter/ui-web';
import Big from 'big.js';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $deleteEntry,
  $itemsInterfaceDisabled,
  $itemsInterfaceFacilityDefaultUnit,
  $itemsInterfaceInventoryLocked,
  $itemsInterfacePricingLocked,
  $itemsInterfaceWeightUnitMode,
  $updateLineItem,
} from '../_state';
import { NormalizedLineItem } from '../_types';
import {
  CameraButtonField,
  DeleteButtonField,
  EstimatedPriceField,
  InventoryItemField,
  PercentageField,
  UnitPriceField,
} from '../fields';
import {
  GRID_COLS_PRICING,
  ITEM_ROW_ERROR_BORDER,
  ITEM_ROW_FOCUS_WITHIN,
} from '../layout';
import { ItemRow } from '../layout/ItemRow';

import { useWeightConversion } from '~/modules/trade/hooks/useWeightConversion';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { toWeight } from '~/modules/trade/utils/weight';

type ItemRowMixedChildProps = {
  lineItem: NormalizedLineItem;
  parentLineItem: NormalizedLineItem;
  autoFocus?: boolean;
  onFocusRow?: (id: string) => void;
  hasError?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ ITEM ROW MIXED CHILD ⚛️ ⚛️ ⚛️
//
export const ItemRowMixedChild: React.FC<ItemRowMixedChildProps> = ({
  lineItem,
  parentLineItem,
  autoFocus = false,
  onFocusRow,
  hasError,
}) => {
  const t = useTranslations();

  // Refs
  const inventorySelectRef = React.useRef<HTMLDivElement>(null);
  const percentageRef = React.useRef<HTMLDivElement>(null);

  // Atoms
  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const pricingLocked = useAtomValue($itemsInterfacePricingLocked);
  const weightUnit = useAtomValue($itemsInterfaceWeightUnitMode);
  const facilityDefaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const onUpdateItem = useSetAtom($updateLineItem);
  const onDeleteItem = useSetAtom($deleteEntry);

  const { convertToDisplayUnit, massUnitAbbreviation } = useWeightConversion({
    weightUnit,
    facilityDefaultUnit,
    pricingUnit: (lineItem.pricingUnit as Unit) ?? enumUnit.Pounds,
  });

  const childNetWeight = lineItem.weights?.[enumLoadWeightType.Net];
  const parentNetWeight = parentLineItem.weights?.[enumLoadWeightType.Net];

  // Memoized values
  const percentage = React.useMemo(() => {
    if (lineItem.percentage != null) return lineItem.percentage;
    const parentNet = parentNetWeight?.value ?? 0;
    const childNet = childNetWeight?.value ?? 0;
    if (parentNet === 0) return 0;
    return new Big(childNet).div(parentNet).times(100).toNumber();
  }, [lineItem.percentage, parentNetWeight?.value, childNetWeight?.value]);

  const weightDisplayValue = React.useMemo(() => {
    if (!childNetWeight) return 0;
    return convertToDisplayUnit(
      toWeight(childNetWeight.value, childNetWeight.unit),
    );
  }, [childNetWeight, convertToDisplayUnit]);

  const inventoryFieldsDisabled = disabled || inventoryLocked;
  const pricingFieldsDisabled = disabled || pricingLocked;

  const baseFieldProps = {
    lineItem,
    onUpdateItem,
    onDeleteItem,
    weightUnit,
    facilityDefaultUnit,
  };

  const inventoryFieldProps = {
    ...baseFieldProps,
    disabled: inventoryFieldsDisabled,
  };

  const pricingFieldProps = {
    ...baseFieldProps,
    disabled: pricingFieldsDisabled,
  };

  // Callbacks
  const handlePercentageChange = React.useCallback(
    (newPercentage: number) => {
      const parentNet = parentNetWeight?.value ?? 0;
      const unit =
        parentNetWeight?.unit ?? lineItem.pricingUnit ?? enumUnit.Pounds;
      const newNetWeight = new Big(parentNet)
        .times(newPercentage)
        .div(100)
        .toNumber();

      onUpdateItem(lineItem.id, {
        percentage: newPercentage,
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Net]: toWeight(newNetWeight, unit),
        },
      });
    },
    [
      lineItem.id,
      lineItem.pricingUnit,
      lineItem.weights,
      parentNetWeight,
      onUpdateItem,
    ],
  );

  // Effects
  /** Auto-focus the percentage input when this row is newly added */
  React.useEffect(() => {
    if (!autoFocus) return;

    const inputElement = percentageRef?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }
  }, [autoFocus]);

  return (
    <ItemRow
      gridTemplate={GRID_COLS_PRICING}
      className={cn(
        '@ItemRowMixedChild relative',
        'after:left-[40px]',
        ITEM_ROW_FOCUS_WITHIN,
        hasError && ITEM_ROW_ERROR_BORDER,
      )}
      onFocus={() => onFocusRow?.(lineItem.id)}
    >
      <div ref={inventorySelectRef} className="col-span-2 pl-[40px]">
        <InventoryItemField {...inventoryFieldProps} lineItem={lineItem} />
      </div>

      <div ref={percentageRef}>
        <PercentageField
          lineItem={lineItem}
          percentage={percentage}
          onPercentageChange={handlePercentageChange}
          disabled={inventoryFieldsDisabled}
        />
      </div>

      <TouchNumberField
        aria-label={t.scaleTicketPro.itemsInterface.childItemWeight}
        isDisabled
        value={weightDisplayValue}
        suffix={massUnitAbbreviation}
        formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
        className="@MixedChildWeightField"
      />

      <UnitPriceField {...pricingFieldProps} />
      <div />
      <EstimatedPriceField {...pricingFieldProps} />

      <div />
      <CameraButtonField {...inventoryFieldProps} disabled />
      <DeleteButtonField {...inventoryFieldProps} />
    </ItemRow>
  );
};
